import { equals, sortBy } from 'rambda';
import { type Reducer, useEffect, useReducer, useRef } from 'react';
import useFriends from '@/hooks/contacts/use-friends';
import useConversationSessions from '@/hooks/sessions/use-conversation-sessions';
import type { ConversationSessionsTypeUnion } from '@/models/conversations/conversation-session';
import type { Friend } from '@/models/user/friend';

export default function useSortedFriends() {
  const { friends, isLoading } = useFriends();

  const prevFriendsRef = useRef<Friend[]>();

  const { availableSessions: recentSessions } = useConversationSessions();

  const [state, dispatch] = useReducer<SortFriendReducer>(
    (state, action) => sortFriendReducer(state, action, { sessions: recentSessions }),
    {
      sortedBy: 'by-name',
      friends: Object.values(friends),
    },
  );

  useEffect(() => {
    const currentFriends = Object.values(friends);

    if (equals(prevFriendsRef.current, currentFriends)) return;

    prevFriendsRef.current = currentFriends;

    dispatch({
      action: 'update-friends',
      friends: currentFriends,
    });
  }, [friends]);

  return {
    isLoading,
    friends,
    sortedFriends: state.friends,
    sortBy: state.sortedBy,
    setSortBy: (filter: SortFriendReducerFilterType) => dispatch({ action: 'change-sort', filter }),
  } as const;
}

export const sortOptions: { value: SortFriendReducerFilterType; label: string }[] = [
  { value: 'by-name', label: 'contact.sort.name' },
  { value: 'by-message-time', label: 'contact.sort.recent-chat' },
  { value: 'by-befriend-time', label: 'contact.sort.recent-join' },
];

export type SortFriendReducerFilterType = 'by-name' | 'by-befriend-time' | 'by-message-time';

type SortFriendReducerAction =
  | { action: 'change-sort'; filter: SortFriendReducerFilterType }
  | { action: 'update-friends'; friends: Friend[] };

type SortFriendReducerState = {
  sortedBy: SortFriendReducerFilterType;
  friends: Friend[];
};

type SortFriendReducer = Reducer<SortFriendReducerState, SortFriendReducerAction>;

function sortFriendReducer(
  state: SortFriendReducerState,
  action: SortFriendReducerAction,
  refs: { sessions: ConversationSessionsTypeUnion[] },
): SortFriendReducerState {
  switch (action.action) {
    case 'change-sort':
      return {
        sortedBy: action.filter,
        friends: sortFriendByFilterType(action.filter, state.friends, refs.sessions),
      };

    case 'update-friends':
      return {
        sortedBy: state.sortedBy,
        friends: sortFriendByFilterType(state.sortedBy, action.friends, refs.sessions),
      };

    default:
      return state;
  }
}

export function sortFriendByFilterType(
  filter: SortFriendReducerFilterType,
  friends: Friend[],
  sessions: ConversationSessionsTypeUnion[],
): Friend[] {
  switch (filter) {
    case 'by-name':
      return sortBy((item) => item.Order, friends);
    case 'by-message-time': {
      const [friendsWithTimestamps, friendsWithoutTimestamps] = friends.reduce<
        [Array<Friend & { lastMessageTimestamp: number }>, Friend[]]
      >(
        (acc, friend) => {
          const session = sessions.find((s) => s.SessionType === 'session' && s.UserID === friend.FriendID);
          if (session?.LastMsgTimeStamp) {
            acc[0].push({ ...friend, lastMessageTimestamp: session.LastMsgTimeStamp });
          } else {
            acc[1].push(friend);
          }
          return acc;
        },
        [[], []],
      );

      return [
        ...sortBy((friend) => -friend.lastMessageTimestamp, friendsWithTimestamps),
        ...sortBy((friend) => friend.Order, friendsWithoutTimestamps),
      ];
    }
    case 'by-befriend-time':
      return sortBy((item) => -item.ID, friends);
    default:
      return friends;
  }
}
